import { DEFAULT_STRAP_DESIGN, editDurationMs, sourceKey, writeGraphicsPack, type EditProject, type GraphicsPackFrame } from '@airpost/shared';
import { loadStill, sourceUrlOf } from './edit-media.js';
import { overlayDraws, strapDraws, textDraws } from './edit-plan.js';
import { FrameRenderer, type LayerImage } from './edit-render.js';
import { loadStrapArt } from './strap-art.js';

/**
 * The graphics of an edit for its render on the playout (ADR 074): the pictures over the sequence, the texts and the
 * straps (in the edit's design, without its clock: ADR 083), drawn by the same FrameRenderer as the monitor and the
 * export, on a clear 1920×1080 canvas, every 20 ms
 * (the fields of 1080i50). A moment that looks like the one before (the same items in the same state: nothing moves)
 * is not drawn again; each different look is drawn once and kept as a PNG, so a still strap is one picture however
 * long it stays. The playout lays the pictures over the pictures it renders from the masters.
 */

export const AIR_GRAPHICS = { width: 1920, height: 1080, stepMs: 20 } as const;

/** What the graphics look like at a moment, as data: two moments with the same key draw the same picture. */
export function graphicsKey(p: EditProject, tMs: number): string {
  const hidden = new Set(p.lanes.hidden);
  return JSON.stringify({
    o: hidden.has('overlay') ? [] : overlayDraws(p, tMs),
    x: hidden.has('text') ? [] : textDraws(p, tMs),
    s: hidden.has('straps') ? [] : strapDraws(p, tMs).map((d) => [d.strap.lines, d.anim]),
  });
}

/** The moments where the look changes, every `stepMs` from 0 to the end: [from, to) with the key of what shows. */
export function graphicsRuns(p: EditProject, stepMs: number = AIR_GRAPHICS.stepMs): Array<{ fromMs: number; toMs: number; key: string }> {
  const total = editDurationMs(p);
  const runs: Array<{ fromMs: number; toMs: number; key: string }> = [];
  for (let t = 0; t < total; t += stepMs) {
    const key = graphicsKey(p, t);
    const last = runs[runs.length - 1];
    const to = Math.min(total, t + stepMs);
    if (last && last.key === key) last.toMs = to;
    else runs.push({ fromMs: t, toMs: to, key });
  }
  return runs;
}

const pngOf = (canvas: HTMLCanvasElement): Promise<Uint8Array> =>
  new Promise((resolve, reject) => canvas.toBlob((b) => (b ? b.arrayBuffer().then((a) => resolve(new Uint8Array(a)), reject) : reject(new Error('PNG'))), 'image/png'));

/** Draw the edit's graphics into a pack (`onProgress` 0..1). */
export async function buildGraphicsPack(p: EditProject, onProgress?: (f: number) => void): Promise<Uint8Array> {
  const { width, height } = AIR_GRAPHICS;
  // the pictures laid over the sequence, loaded before anything is drawn
  const stills = new Map<string, LayerImage | null>();
  for (const o of p.overlays) {
    const key = sourceKey(o.source);
    if (!stills.has(key)) stills.set(key, await loadStill(sourceUrlOf({ kind: 'upload', id: o.source.id })).catch(() => null));
  }
  const missing = [...stills.entries()].filter(([, v]) => !v).map(([k]) => k);
  if (missing.length) throw new Error(`picture not loaded: ${missing.join(', ')}`);
  const designed = p.straps.length > 0 && !p.lanes.hidden.includes('straps');
  if (designed && !(await loadStrapArt(p.strapDesign ?? DEFAULT_STRAP_DESIGN))) throw new Error(`picture not loaded: the straps' design ${p.strapDesign ?? DEFAULT_STRAP_DESIGN}`);
  const renderer = new FrameRenderer(width, height);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const c = canvas.getContext('2d')!;
  const runs = graphicsRuns(p);
  const blobs: Uint8Array[] = [];
  const byKey = new Map<string, number>();
  const frames: GraphicsPackFrame[] = [];
  for (let i = 0; i < runs.length; i++) {
    const r = runs[i]!;
    let blob = byKey.get(r.key);
    if (blob === undefined) {
      renderer.render(c, { project: p, tMs: r.fromMs, spec: { bg: 'black', layers: [] }, imageFor: () => null, stillFor: (k) => stills.get(k) ?? null }, { graphicsOnly: true });
      blob = blobs.push(await pngOf(canvas)) - 1;
      byKey.set(r.key, blob);
    }
    frames.push({ fromMs: r.fromMs, toMs: r.toMs, blob });
    onProgress?.((i + 1) / runs.length);
  }
  return writeGraphicsPack({ width, height, durationMs: editDurationMs(p) }, blobs, frames);
}
